import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/shared/components/ui/dialog';
import { Label } from '@/shared/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/components/ui/select';
import { Textarea } from '@/shared/components/ui/textarea';
import { toast } from 'sonner';
import {
  Calendar, Plus, RefreshCw, CheckCircle, XCircle, AlertCircle, User,
  CalendarDays, CalendarOff, MoreHorizontal } from
'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from
'@/shared/components/ui/dropdown-menu';

const API_URL = API_BASE;
const getAuthHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('ezerpToken')}`
});

const statusColors = {
  draft: 'bg-gray-500/20 text-gray-400',
  submitted: 'bg-blue-500/20 text-blue-400',
  approved: 'bg-green-500/20 text-green-400',
  rejected: 'bg-red-500/20 text-red-400',
  cancelled: 'bg-gray-500/20 text-gray-400'
};

export const LeaveManagement = () => {const { t } = useTranslation("hr");
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [formData, setFormData] = useState({
    employee_id: '', leave_type_id: '', start_date: '', end_date: '', reason: ''
  });

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/hr/leave-requests?`;
      if (statusFilter !== 'all') url += `status=${statusFilter}&`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error('Failed to fetch leave requests');
      const data = await res.json();
      // API returns array directly
      setRequests(Array.isArray(data) ? data : data.requests || []);
    } catch (err) {
      toast.error(err.message);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/employees?status=active`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setEmployees(data.employees || []);
      }
    } catch (err) {console.error(err);}
  };

  const fetchLeaveTypes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/hr/leave-types`, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        // API returns array directly
        setLeaveTypes(Array.isArray(data) ? data : data.leave_types || []);
      }
    } catch (err) {console.error(err);}
  };

  useEffect(() => {fetchRequests();fetchEmployees();fetchLeaveTypes();}, [statusFilter]);

  const handleCreate = async () => {
    try {
      const payload = {
        employee_id: parseInt(formData.employee_id),
        leave_type_id: parseInt(formData.leave_type_id),
        start_date: formData.start_date,
        end_date: formData.end_date,
        reason: formData.reason
      };
      const res = await fetch(`${API_URL}/api/hr/leave-requests`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });
      if (!res.ok) throw new Error('Failed to create leave request');
      toast.success('Leave request created');
      setShowCreate(false);
      setFormData({ employee_id: '', leave_type_id: '', start_date: '', end_date: '', reason: '' });
      fetchRequests();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleSubmit = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/leave-requests/${id}/submit`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to submit');
      toast.success('Leave request submitted');
      fetchRequests();
    } catch (err) {toast.error(err.message);}
  };

  const handleApprove = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/hr/leave-requests/${id}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to approve');
      toast.success('Leave request approved');
      fetchRequests();
    } catch (err) {toast.error(err.message);}
  };

  const handleReject = async (id) => {
    const reason = window.prompt('Enter rejection reason:');
    if (!reason) return;
    try {
      const res = await fetch(`${API_URL}/api/hr/leave-requests/${id}/reject?reason=${encodeURIComponent(reason)}`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Failed to reject');
      toast.success('Leave request rejected');
      fetchRequests();
    } catch (err) {toast.error(err.message);}
  };

  const pendingCount = requests.filter((r) => r.status === 'submitted').length;
  const approvedCount = requests.filter((r) => r.status === 'approved').length;
  const totalDays = requests.filter((r) => r.status === 'approved').reduce((sum, r) => sum + (r.total_days || 0), 0);

  return (
    <div className="space-y-6" data-testid="leave-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("leaveManagement", "Leave Management")}</h1>
          <p className="text-muted-foreground mt-1">{t("manageEmployeeLeave", "Manage employee leave requests and balances")}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="gap-2 bg-green-600 hover:bg-green-700">
          <Plus className="h-4 w-4" />{t("newLeaveRequest", "New Leave Request")}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("totalRequests", "Total Requests")}</p>
                <p className="text-3xl font-bold">{requests.length}</p>
              </div>
              <div className="p-3 rounded-lg bg-blue-500/20"><Calendar className="h-6 w-6 text-blue-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("pendingApproval", "Pending Approval")}</p>
                <p className="text-3xl font-bold">{pendingCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-amber-500/20"><AlertCircle className="h-6 w-6 text-amber-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("approved", "Approved")}</p>
                <p className="text-3xl font-bold">{approvedCount}</p>
              </div>
              <div className="p-3 rounded-lg bg-green-500/20"><CheckCircle className="h-6 w-6 text-green-500" /></div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{t("daysApproved", "Days Approved")}</p>
                <p className="text-3xl font-bold">{totalDays}</p>
              </div>
              <div className="p-3 rounded-lg bg-purple-500/20"><CalendarDays className="h-6 w-6 text-purple-500" /></div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Leave Types Info */}
      {leaveTypes.length > 0 &&
      <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t("leaveTypes", "Leave Types")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              {leaveTypes.map((lt) =>
            <div key={lt.id} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700">
                  <p className="font-medium">{lt.name}</p>
                  <p className="text-sm text-muted-foreground">
                    {lt.default_days_per_year}{t("daysyear", "days/year \u2022")}{lt.is_paid ? 'Paid' : 'Unpaid'}
                  </p>
                </div>
            )}
            </div>
          </CardContent>
        </Card>
      }

      {/* Requests Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{t("leaveRequests", "Leave Requests")}</CardTitle>
            <div className="flex gap-2">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="Filter status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("allStatus", "All Status")}</SelectItem>
                  <SelectItem value="draft">{t("draft", "Draft")}</SelectItem>
                  <SelectItem value="submitted">{t("submitted", "Submitted")}</SelectItem>
                  <SelectItem value="approved">{t("approved", "Approved")}</SelectItem>
                  <SelectItem value="rejected">{t("rejected", "Rejected")}</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={fetchRequests}><RefreshCw className="h-4 w-4" /></Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ?
          <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500" />
            </div> :
          requests.length === 0 ?
          <div className="text-center py-12">
              <CalendarOff className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg text-muted-foreground">{t("noLeaveRequests", "No leave requests found")}</p>
              <Button onClick={() => setShowCreate(true)} className="mt-4 bg-green-600 hover:bg-green-700">
                <Plus className="h-4 w-4 mr-2" />{t("createRequest", "Create Request")}
            </Button>
            </div> :

          <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/50">
                  <tr>
                    <th className="text-left p-3 font-medium">{t("employee", "Employee")}</th>
                    <th className="text-left p-3 font-medium">{t("leaveType", "Leave Type")}</th>
                    <th className="text-left p-3 font-medium">{t("startDate", "Start Date")}</th>
                    <th className="text-left p-3 font-medium">{t("endDate", "End Date")}</th>
                    <th className="text-left p-3 font-medium">{t("days", "Days")}</th>
                    <th className="text-left p-3 font-medium">{t("status", "Status")}</th>
                    <th className="text-right p-3 font-medium">{t("actions", "Actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-700">
                  {requests.map((req) =>
                <tr key={req.id} className="hover:bg-slate-800/30">
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-muted-foreground" />
                          {req.employee_name || `Employee #${req.employee_id}`}
                        </div>
                      </td>
                      <td className="p-3">{req.leave_type_name || req.leave_type_id}</td>
                      <td className="p-3">{req.start_date}</td>
                      <td className="p-3">{req.end_date}</td>
                      <td className="p-3 font-medium">{req.total_days}{t("days", "days")}</td>
                      <td className="p-3">
                        <Badge className={statusColors[req.status]}>{req.status}</Badge>
                      </td>
                      <td className="p-3 text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {req.status === 'draft' &&
                        <DropdownMenuItem onClick={() => handleSubmit(req.id)}>
                                <CheckCircle className="h-4 w-4 mr-2" />{t("submit", "Submit")}
                        </DropdownMenuItem>
                        }
                            {req.status === 'submitted' &&
                        <>
                                <DropdownMenuItem onClick={() => handleApprove(req.id)}>
                                  <CheckCircle className="h-4 w-4 mr-2 text-green-500" />{t("approve", "Approve")}
                          </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleReject(req.id)} className="text-red-400">
                                  <XCircle className="h-4 w-4 mr-2" />{t("reject", "Reject")}
                          </DropdownMenuItem>
                              </>
                        }
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </td>
                    </tr>
                )}
                </tbody>
              </table>
            </div>
          }
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("newLeaveRequest", "New Leave Request")}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <Label>{t("employee", "Employee *")}</Label>
              <Select value={formData.employee_id} onValueChange={(v) => setFormData({ ...formData, employee_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map((emp) =>
                  <SelectItem key={emp.id} value={String(emp.id)}>
                      {emp.full_name || `${emp.first_name} ${emp.last_name}`}
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t("leaveType", "Leave Type *")}</Label>
              <Select value={formData.leave_type_id} onValueChange={(v) => setFormData({ ...formData, leave_type_id: v })}>
                <SelectTrigger><SelectValue placeholder="Select leave type" /></SelectTrigger>
                <SelectContent>
                  {leaveTypes.map((lt) =>
                  <SelectItem key={lt.id} value={String(lt.id)}>{lt.name}</SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t("startDate", "Start Date *")}</Label>
                <Input type="date" value={formData.start_date} onChange={(e) => setFormData({ ...formData, start_date: e.target.value })} />
              </div>
              <div>
                <Label>{t("endDate", "End Date *")}</Label>
                <Input type="date" value={formData.end_date} onChange={(e) => setFormData({ ...formData, end_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>{t("reason", "Reason")}</Label>
              <Textarea value={formData.reason} onChange={(e) => setFormData({ ...formData, reason: e.target.value })} placeholder="Optional reason for leave" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>{t("cancel", "Cancel")}</Button>
            <Button onClick={handleCreate} className="bg-green-600 hover:bg-green-700">{t("createRequest", "Create Request")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>);

};

export default LeaveManagement;